import type { SupabaseClient } from "@supabase/supabase-js";
import { useMutation } from "@tanstack/react-query";

import supabaseClient from "@/api/supabaseClient";
import { queryClient } from "@/main.tsx";
import type { Dna } from "$/models/Dna.d.ts";
import type { UpsertCriticalRequirementsRequest } from "$/requests/UpsertDnaRequest.ts";
import type { User } from "$/models/User";

const useUpsertCriticalRequirements = (refetch = true) => {
  const mutationFn = async (request: UpsertCriticalRequirementsRequest) =>
    upsertCriticalRequirements(supabaseClient, request);

  return useMutation({
    mutationFn,
    onSuccess: async (updatedDna, request) => {
      if (!refetch) return;

      queryClient.setQueryData<User | null>(
        ["user", request.p_user_id],
        (currentUser) => {
          if (!currentUser) return currentUser;

          return {
            ...currentUser,
            dna: updatedDna,
          };
        },
      );
    },
  });
};

export default useUpsertCriticalRequirements;

async function upsertCriticalRequirements(
  client: SupabaseClient,
  request: UpsertCriticalRequirementsRequest,
): Promise<Dna> {
  const { data, error } = await client.rpc(
    "upsert_dna_critical_requirements",
    request,
  );

  if (error) throw new Error(error.message);

  return data;
}
